import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  LayoutDashboard,
  Sparkles,
} from 'lucide-react';
import { channelLabel, channelsToArray } from '../../lib/channels';
import {
  actionsNext7Days,
  BUCKET_LABELS,
  classifyNewsletters,
  summarizeDashboard,
  type ClassifiedNewsletter,
  type DashboardBucket,
  type NewsletterDashboardRow,
} from '../../lib/newsletterDashboard';
import { formatScheduledForDisplay, formatScheduledRelative } from '../../lib/newsletterSchedule';
import { statusPill } from './calendarShared';

function SummaryCard({
  label,
  count,
  tone,
  onClick,
}: {
  label: string;
  count: number;
  tone: 'slate' | 'violet' | 'amber' | 'red' | 'sky' | 'emerald';
  onClick?: () => void;
}) {
  const toneClass = {
    slate: 'border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-900/40',
    violet: 'border-violet-200 dark:border-violet-700 bg-violet-50/80 dark:bg-violet-950/30',
    amber: 'border-amber-200 dark:border-amber-700 bg-amber-50/80 dark:bg-amber-950/30',
    red: 'border-red-200 dark:border-red-800 bg-red-50/80 dark:bg-red-950/30',
    sky: 'border-sky-200 dark:border-sky-700 bg-sky-50/80 dark:bg-sky-950/30',
    emerald: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/80 dark:bg-emerald-950/30',
  }[tone];

  const inner = (
    <>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{count}</div>
      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 leading-snug">{label}</div>
    </>
  );

  if (onClick && count > 0) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`text-left rounded-xl border px-4 py-3 hover:brightness-[0.98] transition-colors ${toneClass}`}
      >
        {inner}
      </button>
    );
  }

  return <div className={`rounded-xl border px-4 py-3 ${toneClass}`}>{inner}</div>;
}

const BUCKET_DISPLAY_ORDER: DashboardBucket[] = [
  'draft_overdue',
  'scheduled_overdue',
  'queued_overdue',
  'draft_planned',
  'scheduled_not_queued',
  'queued_upcoming',
  'draft_no_date',
  'on_hold',
];

export default function NewsletterDashboardView({
  rows,
  loading,
  onRefresh,
}: {
  rows: NewsletterDashboardRow[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const navigate = useNavigate();
  const nowMs = Date.now();

  const classified = useMemo(() => classifyNewsletters(rows, nowMs), [rows, nowMs]);
  const summary = useMemo(() => summarizeDashboard(classified, nowMs), [classified, nowMs]);
  const actionQueue = useMemo(() => actionsNext7Days(classified, nowMs), [classified, nowMs]);

  const byBucket = useMemo(() => {
    const map = new Map<DashboardBucket, ClassifiedNewsletter[]>();
    for (const item of classified) {
      if (item.bucket === 'published_recent' || item.bucket === 'other') continue;
      const list = map.get(item.bucket) ?? [];
      list.push(item);
      map.set(item.bucket, list);
    }
    return map;
  }, [classified]);

  const horizon = useMemo(() => {
    const end30 = nowMs + 30 * 86_400_000;
    return classified.filter(
      (i) =>
        i.sendMs != null &&
        i.sendMs >= nowMs &&
        i.sendMs <= end30 &&
        i.bucket !== 'published_recent',
    );
  }, [classified, nowMs]);

  const openNewsletter = (id: string) => {
    navigate(`/outgoing-newsletters/${id}/content`);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex items-start gap-3">
          <LayoutDashboard className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
            <p>
              <strong className="text-gray-900 dark:text-white">Strategy vs execution:</strong>{' '}
              The calendar shows <em>when</em> sends land. This dashboard shows{' '}
              <em>what needs your decision</em> — commit (Scheduled), queue recipients, and
              optional LinkedIn/site rollout before the send.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Supporting channel work is not tracked in the database yet — use the action hints
              below and the distribute skill in the content repo when a send is 3–10 days out.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryCard label="Draft, no date" count={summary.draftNoDate} tone="slate" />
        <SummaryCard
          label="Not committed"
          count={summary.draftPlanned + summary.draftOverdue}
          tone="slate"
        />
        <SummaryCard
          label="Scheduled, not queued"
          count={summary.scheduledNotQueued + summary.scheduledOverdue}
          tone="violet"
        />
        <SummaryCard label="Urgent (overdue)" count={summary.draftOverdue + summary.scheduledOverdue} tone="red" />
        <SummaryCard label="Queued (next 7d)" count={summary.queuedNext7} tone="sky" />
        <SummaryCard label="On hold" count={summary.onHold} tone="amber" />
      </div>

      <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Actions — next 7 days &amp; overdue
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Sorted by urgency. Open the newsletter to change status or queue recipients.
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
        {loading ? (
          <p className="px-5 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">Loading…</p>
        ) : actionQueue.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500 dark:text-gray-400 text-center">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500 opacity-80" />
            No urgent newsletter actions in the next 7 days.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {actionQueue.slice(0, 25).map(({ item, action }) => (
              <li key={`${item.row.id}-${action.kind}`} className="px-5 py-3 hover:bg-gray-50/80 dark:hover:bg-gray-900/30">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.row.name || 'Untitled'}
                      </span>
                      {item.row.status ? (
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${statusPill(item.row.status)}`}>
                          {item.row.status}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mt-1">
                      {action.label}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{action.detail}</p>
                    {item.row.scheduled_for ? (
                      <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-1">
                        Send {formatScheduledRelative(item.row.scheduled_for, nowMs)} (
                        {formatScheduledForDisplay(item.row.scheduled_for)})
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={() => openNewsletter(item.row.id)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 shrink-0"
                  >
                    Open
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-violet-500" />
              Pipeline buckets
            </h2>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[28rem] overflow-y-auto">
            {BUCKET_DISPLAY_ORDER.filter((b) => (byBucket.get(b)?.length ?? 0) > 0).map((bucket) => {
              const list = byBucket.get(bucket) ?? [];
              return (
              <li key={bucket} className="px-5 py-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {BUCKET_LABELS[bucket]}
                  </span>
                  <span className="text-xs text-gray-500">{list.length}</span>
                </div>
                {list.length === 0 ? null : (
                  <ul className="space-y-1.5">
                    {list.slice(0, 8).map((item) => (
                      <li key={item.row.id}>
                        <button
                          type="button"
                          onClick={() => openNewsletter(item.row.id)}
                          className="w-full text-left text-xs text-gray-800 dark:text-gray-200 hover:text-emerald-600 dark:hover:text-emerald-400 truncate"
                        >
                          {item.row.name || 'Untitled'}
                          {item.row.scheduled_for
                            ? ` · ${formatScheduledRelative(item.row.scheduled_for, nowMs)}`
                            : ''}
                        </button>
                      </li>
                    ))}
                    {list.length > 8 ? (
                      <li className="text-[11px] text-gray-500">+{list.length - 8} more</li>
                    ) : null}
                  </ul>
                )}
              </li>
              );
            })}
            {byBucket.size === 0 ? (
              <li className="px-5 py-6 text-sm text-gray-500 dark:text-gray-400">No active pipeline items.</li>
            ) : null}
          </ul>
        </section>

        <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-500" />
              30-day horizon
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {summary.horizon30} send{summary.horizon30 === 1 ? '' : 's'} with a planned date
            </p>
          </div>
          {horizon.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-500 dark:text-gray-400">No upcoming sends in the next 30 days.</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[28rem] overflow-y-auto">
              {horizon.map((item) => {
                const channels = channelsToArray(item.row.channels);
                return (
                  <li key={item.row.id} className="px-5 py-3">
                    <button
                      type="button"
                      onClick={() => openNewsletter(item.row.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {item.row.name || 'Untitled'}
                        </span>
                        {item.row.status ? (
                          <span className={`shrink-0 inline-flex px-2 py-0.5 rounded text-[10px] font-medium ${statusPill(item.row.status)}`}>
                            {item.row.status}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {formatScheduledForDisplay(item.row.scheduled_for)}
                      </p>
                      {channels.length > 0 ? (
                        <p className="text-[10px] text-gray-500 mt-1 truncate">
                          {channels.map((c) => channelLabel(c)).join(', ')}
                        </p>
                      ) : (
                        <p className="text-[10px] text-gray-500 mt-1">All subscribers</p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <section className="rounded-xl border border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20 p-5">
        <h2 className="text-sm font-semibold text-violet-950 dark:text-violet-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Distribution rhythm (manual today)
        </h2>
        <p className="text-xs text-violet-900/80 dark:text-violet-200/80 mt-2 leading-relaxed">
          When you set a send date, consider this cadence in the content repo (distribute skill):
        </p>
        <ol className="mt-3 space-y-2 text-xs text-violet-950 dark:text-violet-100 list-decimal list-inside">
          <li>
            <strong>T−14 to T−7:</strong> LinkedIn feed + comment from canonical URL (tease, don’t
            duplicate).
          </li>
          <li>
            <strong>T−7:</strong> Set status <strong>Scheduled</strong> in engine when copy is final.
          </li>
          <li>
            <strong>T−24h:</strong> Recipients auto-queue (or queue manually); review Recipients tab.
          </li>
          <li>
            <strong>Send day:</strong> Cron delivers; tracking on Recipients / Tracking tabs.
          </li>
        </ol>
        <p className="text-[11px] text-violet-800/70 dark:text-violet-300/70 mt-3">
          Future: optional distribution tasks table linked to newsletter_id — not built yet.
        </p>
      </section>
    </div>
  );
}
